/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title, Meta, DomSanitizer } from '@angular/platform-browser';
import { InstanceService } from '@commonspirit/ngx-instance';
import { CjaTagService } from 'src/app/shared/services/cja-tag/cja-tag.service';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { IProviderParams, ISearchParams } from 'src/app/shared/models/v1/location';
import { LocationService } from 'src/app/shared/services/locations/location.service';
import { SearchService } from 'src/app/shared/services/search/search.service';
import { TagHandlerService } from 'src/app/shared/services/tag-handler/tag-handler.service';
import { Location as WindowLocation } from '@angular/common';

import * as fromLocations from './+state';
import {
  AssociatedProvider,
  Location,
  LocationHour,
  LocationSpecialty,
  navigateFAD,
  ProviderFromFAD,
  SessionConstants
} from './../../shared/models/v2/search';
import { SearchActions } from '../search/+state';
import { EnvironmentService } from '@commonspirit/ngx-environment';
import { EnvValue } from '@commonspirit/ngx-environment/lib/models/key-value-pair';
@Component({
  selector: 'fal-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LocationComponent implements OnInit, OnDestroy {
  public location$: Observable<Location | undefined> = new Observable<Location>();
  private locationSubscription: Subscription;
  private fadProviderSub: Subscription;
  public providers$: Observable<AssociatedProvider[] | undefined> = new Observable<AssociatedProvider[]>();
  public locationHourSets: Array<LocationHour>;
  public labHourSets = [] as any;
  public officeHourSets = [] as any;
  public holidayHours: string[] = [];
  public expanderExpanded = false;
  public showMoreActive = false;
  public services: string[] = [];
  public showBackButton = true;
  public locationName: string | undefined = '';
  public locationId: string | number = '';
  public locationSlug: string;
  public showMoreHours = false;
  public heroImageUrl: string | undefined = '';
  public showFadProvider = false;
  public location: Location;
  public htmlSideNavigationContent = '';
  public isVisibleSideNavigation = false;
  public isHospitalBased = false;
  public showSpinner = false;
  public emergencyNotifications: string[];
  public emergencyHeader?: string;
  public showEmergencies = false;
  public showModal = false;
  public emergencyBody?: string;
  public showViewMore?: boolean = false;
  public showErrorPage = false;
  navigateFADModel: navigateFAD = {
    npi: '',
    locationId: '',
    locationName: '',
    city: '',
    state: ''
  };
  public locationNpis: string[] = [];
  public fadProviders: ProviderFromFAD[] = [];
  public fadProvider: ProviderFromFAD;
  public sideNavigationLocationIds: string[] = [];
  public marketCode = '';
  public selfUrl: string;
  enableBlockIT: boolean;
  constructor(
    private store: Store,
    private searchService: SearchService,
    private tagHandlerService: TagHandlerService,
    public instanceService: InstanceService,
    private router: Router,
    private route: ActivatedRoute,
    private cjaTagService: CjaTagService,
    private locationService: LocationService,
    public renderer: Renderer2,
    public sanitizer: DomSanitizer,
    private titleService: Title,
    private metaService: Meta,
    private windowLocation: WindowLocation,
    public environmentService: EnvironmentService
  ) {
    const enableBlockITForEnvironment: EnvValue | undefined = this.environmentService.getSetting('enableBlockIT')?.value;
    if (typeof enableBlockITForEnvironment === 'boolean') {
      this.enableBlockIT = enableBlockITForEnvironment;
    }
  }

  ngOnInit(): void {
    this.showErrorPage = false;
    this.marketCode = this.instanceService.getConfiguration(ConfigurationKey.MarketCodes)?.value as string;
    this.location$ = this.store.select(fromLocations.getSelectedLocation);
    this.locationId = this.route.snapshot.params.id;
    this.locationSlug = this.route.snapshot.params.slug;
    this.showProviderDetails();
    this.sideNavigationLocationIds = this.instanceService.getConfiguration(ConfigurationKey.SideNavigationLocationIds)?.value as [];

    this.locationSubscription = this.location$.subscribe((location) => {
      if (location) {
        if (this.enableBlockIT ? location.slug != this.locationSlug : location.id != this.locationId) {
          this.getLocation();
        } else {
          this.location = location;
          this.setLocationDetails(this.location);
          this.getXFContent(this.location.externalCode, this.sideNavigationLocationIds);
          this.cjaTagService.logCJAEvent(this.createAdobeCJALink());
        }
      } else if (!!SessionConstants.SelectedLocation) {
        const locationDetails: string = sessionStorage.getItem(SessionConstants.SelectedLocation) ?? '{}';
        if (locationDetails != '{}') {
          this.location = JSON.parse(locationDetails.replace("'", ''));
          if (this.enableBlockIT ? this.locationSlug != this.location.slug : this.locationId != this.location.id) {
            this.getLocation();
          } else {
            this.setLocationDetails(this.location);
            this.getXFContent(this.location.externalCode, this.sideNavigationLocationIds);
            this.cjaTagService.logCJAEvent(this.createAdobeCJALink());
          }
        } else {
          this.getLocation();
        }
      }
    });
    this.processProviders();
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setPageTitle(location: Location) {
    let title = location.name + ' in ' + location.address.city + ' , ' + location.address.state;
    title += this.marketCode ? ` | ${this.marketCode.toUpperCase()}` : '';
    this.titleService.setTitle(title);
    this.tagHandlerService.setLocationDetailsMeta(location, title, this.heroImageUrl ?? '', this.services);
  }
  toggleModal(): void {
    this.showModal = !this.showModal;
  }

  onClickedOutside(): void {
    this.showModal = false;
  }

  createAdobeCJALink(): any {
    return {
      event: 'customEvent',
      event_name: 'find_location:location page view',
      event_category: 'find a location',
      event_action: 'location page view',
      event_label: '',
      event_facility: this.location.name, //name of the facility if available
      division: '', //mapped division name, if avail
      medicalGroup: '', //medical group name, if avail
      market: '', //market name, if avail
      patientEngagement: 'true',
      visitor_loginStatus: 'false' //true or false based on if the user is logged in
    };
  }

  getLocation(): void {
    this.showSpinner = true;
    const searchParams: ISearchParams = {
      searchTerm: this.enableBlockIT ? '' : this.locationId.toString(),
      location: this.enableBlockIT ? this.locationSlug : '',
      sortBy: null,
      specialties: '',
      locationType: '',
      distance: null,
      latitude: null,
      longitude: null,
      city: '',
      state: '',
      zipcode: ''
    };
    this.searchService
      .query(searchParams)
      .pipe(
        tap((searchResults) => {
          if (searchResults.results?.length == 0) {
            this.showErrorPage = true;
            return;
          }
          const searchUrlSessionKey = 'searchUrl';
          const backUrl = sessionStorage.getItem(searchUrlSessionKey);
          if (!backUrl) this.showBackButton = false;
          this.location = searchResults.results.find((x) => {
            return this.enableBlockIT ? x.slug == this.locationSlug : x.id == this.locationId;
          }) as Location;
          this.showSpinner = false;
          this.getXFContent(this.location.externalCode, this.sideNavigationLocationIds);
          this.setLocationDetails(this.location);
          return;
        }),
        catchError((error) => {
          this.showErrorPage = true;
          return of(SearchActions.loadSearchFailure(error));
        })
      )
      .subscribe();
  }

  getXFContent(externalCode: string, locationIds: string[]): void {
    if (locationIds.includes(externalCode.toLocaleLowerCase())) {
      this.showSpinner = true;
      this.locationService.loadXF(externalCode.toLocaleLowerCase()).subscribe(
        (result): void => {
          this.htmlSideNavigationContent = result;
          this.isVisibleSideNavigation = true;
          this.showSpinner = false;
        },
        (error: Error) => {
          console.log(`no result: ${error.message}`);
          this.showSpinner = false;
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.locationSubscription.unsubscribe();
    this.fadProviderSub.unsubscribe();
    this.tagHandlerService.removeschema();
    this.tagHandlerService.removePageCanonicals(this.selfUrl);
  }

  processProviders(): void {
    const providerRequestParams: IProviderParams = this.getProviderRequest();
    this.showSpinner = true;
    this.fadProviderSub = this.searchService.LoadAssociatedProvider(providerRequestParams).subscribe(
      (result) => {
        const providerObj = result;
        this.shuffleProviders(providerObj);
        providerObj.forEach((item: any) => {
          this.fadProvider = {
            displayFullName: item.displayFullName,
            npi: item.npi,
            acceptingNewPatients: item.acceptsNewPatients,
            primarySpecialty: item.primarySpecialty,
            imageSmall: '',
            slug: item.slug,
            secondarySpecialty: [],
            firstName: item.firstName,
            lastName: item.lastName,
            medicalGroupIds: [],
            gender: item.gender
          };

          item.images?.forEach((img: any) => {
            if (img.imageType == 'small') {
              this.fadProvider.imageSmall = img.url;
            }
          });

          item.specialties = item.specialties?.filter((secondarySpeciality: string) => {
            return secondarySpeciality != item.primarySpecialty;
          });

          item.specialties?.forEach((element: string) => {
            this.fadProvider.secondarySpecialty.push(element);
          });

          item.medicalGroupIds?.forEach((grpId: string) => {
            this.fadProvider.medicalGroupIds.push(grpId);
          });

          this.fadProviders.push(this.fadProvider);
        });

        this.showSpinner = false;
      },
      (error) => {
        this.showSpinner = false;
        console.log('no result');
      }
    );
  }

  shuffleProviders(providers: ProviderFromFAD[]): ProviderFromFAD[] {
    let p = providers.length;
    let fObj: ProviderFromFAD;
    let i = 3;

    while (p) {
      i = Math.floor(Math.random() * p--);
      fObj = providers[p];
      providers[p] = providers[i];
      providers[i] = fObj;
    }
    return providers;
  }

  getProviderRequest(): IProviderParams {
    const divisionCode = this.instanceService.getConfiguration(ConfigurationKey.DivisionCodes)?.value as string;
    const marketCode = this.instanceService.getConfiguration(ConfigurationKey.MarketCodes)?.value as string;
    const providerRequestParams: IProviderParams = {
      locationId: this.enableBlockIT ? this.locationSlug : (this.locationId as string),
      divisionCode: divisionCode,
      marketCode: marketCode
    };
    return providerRequestParams;
  }

  setHolidayHoursInfo(): void {
    this.holidayHours = [
      'Memorial Day (May 31, 2021): Closed',
      'Independence Day (Observed July 5, 2021): Closed',
      'Labor Day (September 6, 2021): Closed',
      'Thanksgiving Day (November 25, 2021): Closed',
      'Thanksgiving - Day-After (November 26, 2021): Open',
      'Christmas Eve (December 24, 2021): Closed',
      'Christmas Day (December 25, 2021): Closed',
      'New Years Eve (December 31, 2021): Closed',
      '2022 New Years Day (January 1, 2022): Closed'
    ];
  }

  setLocationHourSets(location: Location | undefined): void {
    const hours = location?.locationHours.filter((x: any) => x.officeHourType === null) as LocationHour[];
    this.locationHourSets = hours;
  }

  setOfficeHourSets(location: Location | undefined): void {
    const hours = location?.locationHours.filter((x: any) => x.officeHourType === 'Office Hours') as LocationHour[];
    this.officeHourSets = hours;
  }

  setLabHourSets(location: Location | undefined): void {
    const hours = location?.locationHours.filter((x: any) => x.officeHourType === 'Lab Hours');
    this.labHourSets = hours;
  }

  setEmergencyNotifications(notifications: string[]): void {
    if (!Boolean(notifications?.hasValue())) {
      this.showEmergencies = false;
      return;
    }

    this.emergencyNotifications = notifications;
    this.showEmergencies = true;
    const split = notifications[0].split('|');
    const copy = [...split];
    if (copy.length > 1) this.showViewMore = true;
    this.emergencyHeader = copy[1];
    this.emergencyBody = copy[0];
  }

  toggleShowEmergencies(): void {
    this.showEmergencies = !this.showEmergencies;
  }

  setHeroImageUrl(location: Location | undefined): void {
    if (
      !location?.headerImage ||
      location?.headerImage.length === 0 ||
      !location?.headerImage[0] ||
      !location?.headerImage[0]?.imageUrl ||
      !location?.headerImage[0]?.imageUrl[0]
    ) {
      return;
    }

    this.heroImageUrl = location?.headerImage[0]?.imageUrl[0];
  }

  setServices(location: Location | undefined): void {
    if (!location?.services) {
      return;
    }
    this.services = location.services.map((service: LocationSpecialty) => {
      return service.specialty;
    });
  }

  async backToSearch(): Promise<boolean> {
    // if a user navigated to FAD and returns to FAL, browser history will contain more items.
    // but we need to go to search page from here, so grab it from session.
    // no need to store it in state.
    const searchUrlSessionKey = 'searchUrl';
    const backUrl = sessionStorage.getItem(searchUrlSessionKey);
    if (backUrl) {
      // going back to search results, so reset
      sessionStorage.removeItem(searchUrlSessionKey);
      return await this.router.navigateByUrl(backUrl);
    }

    return false;
  }

  toggleExpanded(): void {
    this.expanderExpanded = !this.expanderExpanded;
  }

  showProviderDetails(): void {
    const npiQueryParam = this.route.snapshot.queryParams;

    if (npiQueryParam && npiQueryParam.npi) {
      this.navigateFADModel.npi = npiQueryParam.npi;
      this.navigateFADModel.locationId = this.locationSlug;
      this.handleProviderNavigateFAD(this.navigateFADModel).catch(() => console.log('Error navigating to FAD.'));
    }
  }

  async handleProviderNavigateFAD(event: navigateFAD): Promise<boolean> {
    this.navigateFADModel = event;
    this.showFadProvider = true;
    return await this.router.navigate([], { queryParams: { npi: event.npi } });
  }

  setStandardizeUrl(location: Location): void {
    if (location) {
      const loctionUrl = `/${this.location?.name
        .toUrlFriendly()
        .stripPeriod()
        .condenseDashes()
        .removeSpecialCharacters()
        .sanitizeHyphens()}-${this.location?.id}`;
      const fullUrl = `${window.location.origin}${loctionUrl}`;
      if (window.location.href != fullUrl) {
        this.windowLocation.replaceState(loctionUrl);
      }
    }
    this.selfUrl = window.location.href;
    this.tagHandlerService.setPageCanonicals(window.location.href);
    this.tagHandlerService.setLocationSchema(location);
  }

  setLocationDetails(location: Location): void {
    this.setEmergencyNotifications(location?.emergencyNotifications ?? []);
    this.setLocationHourSets(location);
    this.setOfficeHourSets(location);
    this.setLabHourSets(location);
    this.setHeroImageUrl(location);
    this.setServices(location);
    this.setPageTitle(location);
   // this.setStandardizeUrl(location);
    this.locationName = location?.name;
    this.locationId = location?.id ?? '';
    this.holidayHours = location?.holidayHours?.filter((hours) => hours != null && hours != '' && hours != undefined) ?? [];
    const hospitalBased = this.instanceService.getConfiguration(ConfigurationKey.HospitalBasedLocation);
    if (hospitalBased && hospitalBased.value) {
      const hospitalBasedTypes = hospitalBased.value as string[];
      this.isHospitalBased = hospitalBasedTypes.includes(location.type);
    }
  }
}
